import apiClient from '../Api';
import { extractData, isSuccess, getErrorMessage } from '../../utils/apiHelpers';
import { summarizeRawApiResult, logStringCopySave } from '../../features/panel-planning/utils/stringCopySaveDebug.js';
import { panelPerfMark, panelPerfMeasure, panelPerfCountSummary } from '../../features/panel-planning/utils/panelPlanningPerfLog.js';
import {
  extractSolarArraySeqFromHalUri,
  resolveSolarSectionSeqFromApiPayload
} from '../../features/panel-planning/utils/panelSiteDesignPatch.js';

/**
 * 사이트 디자인 관련 API 클라이언트
 */
const siteDesignApi = {
  /**
   * 사이트 디자인 정보를 조회합니다.
   * @param {string} siteId - 사이트 ID
   * @returns {Promise<Object>} 사이트 디자인 데이터
   */
  getSiteDesign: async siteId => {
    panelPerfMark('getSiteDesign:http', { siteId });
    try {
      const response = await apiClient.read(`/site-design/${siteId}`);
      panelPerfMeasure('getSiteDesign:http', 'getSiteDesign:http', {
        siteId,
        status: response?.status
      });

      console.log('Raw API response:', response);

      const data = extractData(response);
      const success = isSuccess(response);

      if (success) {
        console.log('Extracted site design data:', data);
        panelPerfCountSummary('getSiteDesign:payload', {
          siteId,
          topLevelKeys: data && typeof data === 'object' ? Object.keys(data) : []
        });
        return data;
      } else {
        const errorMessage = getErrorMessage(response);
        console.error('API Error:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Site design API error:', error); 
      throw error;
    }
  },

  /**
   * 사이트에 솔라 스트링과 모듈을 추가합니다.
   * @param {string} siteId - 사이트 ID
   * @param {Object} data - 솔라 스트링 데이터
   * @returns {Promise<Object>} 추가된 솔라 스트링 데이터
   */
  addSolarStringAndItsModules: async (siteId, data) => {
    try {
      console.log('📤 API 호출 - URL:', `/site-design/${siteId}/solar-string`);

      const response = await apiClient.create(`/site-design/${siteId}/solar-string`, data);
      const body = typeof response?.data === 'string' ? JSON.parse(response.data) : response?.data;
      const extracted = extractData(body ?? response);
      logStringCopySave('addSolarStringAndItsModules → HTTP response', {
        siteId,
        status: response?.status,
        bodyKeys: body && typeof body === 'object' ? Object.keys(body) : [],
        extractedSummary: summarizeRawApiResult(extracted ?? body ?? response?.data),
        requestStringId: data?.stringId ?? null
      });
      if (extracted && typeof extracted === 'object' && (extracted.id != null || extracted.seq != null)) {
        return extracted;
      }
      return body ?? response?.data ?? extracted;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        const authHint =
          '인증이 필요합니다. Network 탭에서 Authorization 헤더와 siteId를 확인한 뒤, 로그아웃 후 다시 로그인해 주세요.';
        console.error('❌ Solar string API error (403):', authHint, { siteId });
        throw new Error(authHint);
      }
      console.error('❌ Solar string API error:', error);
      throw error;
    }
  },

  /**
   * 솔라 스트링 + 하위 모듈 삭제 (SiteDesign API).
   * DELETE /api/site-design/{siteId}/solar-string/{solarStringSeq}
   *
   * @param {string|number} siteId - 사이트 PK (site.seq)
   * @param {string|number} solarStringSeq - String entity PK (JSON `id`, DB `solar_string.seq`) — stringId 아님
   * @returns {Promise<Object>} 삭제 결과
   */
  deleteSolarString: async (siteId, solarStringSeq) => {
    const siteKey = siteId != null && siteId !== '' ? String(siteId) : null;
    const stringKey =
      solarStringSeq != null && solarStringSeq !== '' ? String(solarStringSeq) : null;
    if (!siteKey || !stringKey) {
      throw new Error('siteId and solarStringSeq are required for string delete');
    }
    try {
      const response = await apiClient.delete(
        `/site-design/${siteKey}/solar-string/${stringKey}`
      );
      console.log('Delete solar string response:', response);

      const resultData = extractData(response);
      const success = isSuccess(response);

      if (success) {
        console.log('Solar string deleted successfully:', resultData);
        return resultData;
      } else {
        const errorMessage = getErrorMessage(response);
        console.error('Delete API Error:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Delete solar string API error:', error);
      throw error;
    }
  },

  /**
   * 솔라 모듈을 삭제합니다.
   * @param {string} siteId - 사이트 ID (미사용, Spring Data REST는 siteId 불필요)
   * @param {string|number} moduleId - 삭제할 솔라 모듈 entity PK (seq, JSON `id`)
   * @returns {Promise<Object>} 삭제 결과
   */
  deleteSolarModule: async (siteId, moduleId) => {
    try {
      const response = await apiClient.delete(`/solarModules/${moduleId}`);
      return response.data;
    } catch (error) {
      console.error('Delete solar module API error:', error);
      throw error;
    }
  },

  /**
   * 솔라 어레이를 추가합니다 (Spring Data REST).
   * siteId로 Section을 찾고, 없으면 생성 후 Array를 추가한다.
   * @param {string} siteId - 사이트 ID (Section 검색/생성용)
   * @param {Object} arrayData - 솔라 어레이 데이터
   * @returns {Promise<Object>} 추가된 솔라 어레이 데이터
   */
  addSolarArray: async (siteId, arrayData, sectionId = null) => {
    try {
      let secId = sectionId;

      // sectionId가 없으면 검색/생성 (폴백)
      if (!secId) {
        try {
          const sectionSearch = await apiClient.read(
            `/solarSections/search/findTopBySiteSeqOrderBySeqDesc?siteSeq=${siteId}`
          );
          secId = resolveSolarSectionSeqFromApiPayload(
            sectionSearch?.data,
            sectionSearch?.headers
          );
        } catch (e) {
          // 404 → 아래에서 생성
        }

        if (!secId) {
          const newSection = await apiClient.create('/solarSections', {
            siteId,
            name: 'Default Section'
          });
          secId = resolveSolarSectionSeqFromApiPayload(
            newSection?.data,
            newSection?.headers
          );
        }
      }

      if (!secId) {
        throw new Error('Failed to find or create SolarSection');
      }

      const payload = {
        ...arrayData,
        solarSection: `/api/solarSections/${secId}`
      };

      console.log('📤 Array 생성 요청:', payload);
      const response = await apiClient.create('/solarArrays', payload);
      const body = response.data;
      const locationSeq = extractSolarArraySeqFromHalUri(response.headers?.location);
      if (
        body &&
        typeof body === 'object' &&
        locationSeq != null &&
        body.id == null &&
        body.seq == null
      ) {
        return { ...body, id: locationSeq, _sectionSeq: secId };
      }
      if (body && typeof body === 'object') {
        return { ...body, _sectionSeq: secId };
      }
      return body;
    } catch (error) {
      console.error('❌ Add solar array API error:', error);
      throw error;
    }
  },

  /**
   * 솔라 어레이를 삭제합니다 (Spring Data REST).
   * @param {string} siteId - 사이트 ID (미사용)
   * @param {string} arrayId - 삭제할 솔라 어레이 ID
   * @returns {Promise<Object>} 삭제 결과
   */
  deleteSolarArray: async (siteId, arrayId) => {
    try {
      const response = await apiClient.delete(`/solarArrays/${arrayId}`);
      return response.data;
    } catch (error) {
      console.error('Delete solar array API error:', error);
      throw error;
    }
  },

  /**
   * 솔라 어레이를 업데이트합니다 (Spring Data REST).
   * @param {string} siteId - 사이트 ID (미사용)
   * @param {string} arrayId - 업데이트할 솔라 어레이 ID
   * @param {Object} arrayData - 업데이트할 어레이 데이터
   * @returns {Promise<Object>} 업데이트 결과
   */
  updateSolarArray: async (siteId, arrayId, arrayData) => {
    try {
      console.log('✏️ Array 업데이트 요청:', arrayId, arrayData);
      const response = await apiClient.update(`/solarArrays/${arrayId}`, arrayData);
      return response.data;
    } catch (error) {
      console.error('❌ Update solar array API error:', error);
      throw error;
    }
  }
};

export default siteDesignApi;
